import { useEffect, useCallback, useRef } from 'react';
import { getCanonicalUrl, getScreenFromUrl } from '../lib/routes';

/**
 * Hook to synchronize screen state with browser URL
 * Provides canonical URLs and handles browser navigation
 */
export function useUrlSync(currentScreen: string, onNavigate: (screen: string) => void) {
  // CB:BEGIN urlsync-initial-guard
  const hasMountedRef = useRef(false);
  // CB:END urlsync-initial-guard
  
  // Update URL when screen changes
  useEffect(() => {
    // CB:BEGIN urlsync-initial-guard
    // Avoid clobbering direct deep links (e.g., /kpi) on initial mount.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    // CB:END urlsync-initial-guard

    // CB:BEGIN preserve-next-steps-url
    // Don't change URL if we're on /next-steps - preserve it for signup flow
    if (window.location.pathname === '/next-steps') {
      return;
    }
    // CB:END preserve-next-steps-url
    // CB:BEGIN preserve-sign-in-url
    if (window.location.pathname === '/sign-in' || window.location.pathname.startsWith('/sign-in?')) {
      return;
    }
    // CB:END preserve-sign-in-url

    // CB:BEGIN preserve-invite-urls-in-sync
    // CRITICAL: Don't change URL if we're on an invite URL with token
    const currentPath = window.location.pathname;
    if (currentPath.match(/^\/invite\/[^/]+/)) {
      console.log('🔒 useUrlSync: Preserving invite URL with token:', currentPath);
      return;
    }
    // CB:END preserve-invite-urls-in-sync

    const canonicalUrl = getCanonicalUrl(currentScreen);
    if (canonicalUrl && window.location.pathname !== canonicalUrl) {
      window.history.pushState({ screen: currentScreen }, '', canonicalUrl);

      // Update document title and meta description
      const route = getCanonicalUrl(currentScreen);
      if (route) {
        document.title = route;
      }
    }
  }, [currentScreen]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.screen) {
        onNavigate(event.state.screen);
      } else {
        // Handle direct URL access
        const pathname = window.location.pathname;
        const screen = getScreenFromUrl(pathname);
        if (screen && screen !== currentScreen) {
          onNavigate(screen);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [currentScreen, onNavigate]);

  // Handle direct URL access on mount
  useEffect(() => {
    const pathname = window.location.pathname;
    if (pathname !== '/') {
      const screen = getScreenFromUrl(pathname);
      if (screen && screen !== currentScreen) {
        // CB:BEGIN urlsync-deeplink-canonicalize
        // Navigate to internal screen AND rewrite to canonical path immediately
        onNavigate(screen);
        const canonical = getCanonicalUrl(screen);

        // CB:BEGIN preserve-invite-token-in-urlsync
        // CRITICAL: Never strip invite tokens! Only canonicalize if paths actually differ
        // and we're not on a dynamic route (invite, contractor/projects, etc.)
        const isDynamicRoute = pathname.match(/^\/invite\/[^/]+/) ||
                               pathname.match(/^\/contractor\/projects\/[^/]+/);

        if (!isDynamicRoute && canonical && canonical !== pathname) {
          window.history.replaceState({ screen }, '', canonical);
        }
        // CB:END preserve-invite-token-in-urlsync
        // CB:END urlsync-deeplink-canonicalize
      }
    }
  }, []); // Only run on mount

  // Function to navigate with URL update
  const navigateWithUrl = useCallback((screen: string, data?: any) => {
    onNavigate(screen, data);
  }, [onNavigate]);

  return { navigateWithUrl };
}
